import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import RelatedMovies from '@/pages/detail/components/RelatedMovies';
import EmptyState from '@/components/base/EmptyState';
import { formatViews, getMovieById, getRelatedMovies } from '@/lib/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo({
    title: movie ? `${movie.title} - 麻豆传媒视频` : '影视未找到 - 麻豆传媒视频',
    description: movie ? `${movie.summary} 类型：${movie.type}／${movie.genre}，地区：${movie.region}。` : '你访问的影视内容不存在或已下架。',
    keywords: movie ? `${movie.title},${movie.genre},${movie.type},${movie.tags.join(',')}` : '影视详情',
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 1800);
  };

  if (!movie) {
    return (
      <div className="mx-auto w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="抱歉，找不到这部影视作品"
          description="该影视可能已下架、链接有误，或资源暂时加载失败。你可以返回首页或前往分类页继续浏览。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/category" className="rounded-md border border-background-300 px-5 py-2 text-sm text-foreground-800 hover:border-primary-400 whitespace-nowrap">
            浏览影视分类
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);

  const metaRows = [
    { label: '类型', value: movie.type },
    { label: '题材', value: movie.genre },
    { label: '地区', value: movie.region },
    { label: '年份', value: `${movie.year}` },
    { label: '时长', value: movie.duration },
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join('、') },
    { label: '状态', value: movie.status },
  ];

  return (
    <div className="w-full">
      <div className="bg-primary-900">
        <div className="mx-auto w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300/80" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category?type=${encodeURIComponent(movie.type)}`} className="hover:text-accent-300">
              {movie.type}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">{movie.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-10">
            <div className="w-full max-w-[240px] shrink-0 md:w-[260px]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-100/10 bg-primary-800">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 封面大图`}
                  title={`${movie.title} 影视封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">{movie.type}</span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-100">{movie.genre}</span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-100">{movie.status}</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1 text-accent-300">
                  <i className="ri-fire-line"></i>
                  {formatViews(movie.views)} 热度
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {movie.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-timer-line"></i>
                  {movie.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-global-line"></i>
                  {movie.region}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?genre=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-100/20 px-3 py-1 text-xs text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300 whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const now = toggleFavorite(movie.id);
                    showToast(now ? '已加入收藏' : '已取消收藏');
                  }}
                  className={`flex items-center gap-2 rounded-md px-6 py-2.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited ? 'bg-background-50 text-primary-800' : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  }`}
                >
                  <i className={favorited ? 'ri-bookmark-fill text-lg' : 'ri-bookmark-line text-lg'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => showToast('分享链接已生成（前端演示）')}
                  className="flex items-center gap-2 rounded-md border border-background-100/30 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-line text-lg"></i>
                  分享
                </button>
              </div>

              <p className="mt-4 text-xs text-background-300/70">
                <i className="ri-information-line mr-1"></i>
                本页为演示内容，不含在线播放、下载与付费功能，收藏状态保存在本地浏览器。
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full px-4 md:px-8 py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">影视简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">{movie.summary}</p>
            </section>

            <RelatedMovies list={related} />
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">作品信息</h2>
              <dl className="mt-4 flex flex-col gap-3">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-12 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">所属分类</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/category?type=${encodeURIComponent(movie.type)}`}
                  className="rounded-md bg-primary-100 px-3 py-1.5 text-xs text-primary-700 transition-colors hover:bg-primary-200 whitespace-nowrap"
                >
                  {movie.type}
                </Link>
                <Link
                  to={`/category?genre=${encodeURIComponent(movie.genre)}`}
                  className="rounded-md bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 whitespace-nowrap"
                >
                  {movie.genre}
                </Link>
                <Link
                  to={`/category?region=${encodeURIComponent(movie.region)}`}
                  className="rounded-md bg-accent-100 px-3 py-1.5 text-xs text-accent-800 transition-colors hover:bg-accent-200 whitespace-nowrap"
                >
                  {movie.region}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}